import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { DEFAULT_HEALTH_STATUSES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Health status dropdown. Sources options from DEFAULT_HEALTH_STATUSES + custom
 * topics with category="health_status". Faculty/Manager can add new entries inline.
 */
export default function HealthStatusSelect({ value, onChange, testId = "hs-select", placeholder = "Select health status…", allowEmpty = true }) {
  const { user } = useAuth();
  const canAdd = user?.role === "faculty" || user?.role === "manager";
  const [options, setOptions] = useState(DEFAULT_HEALTH_STATUSES);
  const [addOpen, setAddOpen] = useState(false);
  const [newVal, setNewVal] = useState("");

  useEffect(() => {
    api.get("/topics?category=health_status").then((r) => {
      const fromApi = r.data.map((t) => t.name);
      setOptions(Array.from(new Set([...DEFAULT_HEALTH_STATUSES, ...fromApi])));
    }).catch(() => {});
  }, []);

  const handleAdd = async () => {
    const name = newVal.trim();
    if (!name) return;
    try {
      await api.post("/topics", { name, category: "health_status" });
      setOptions((prev) => Array.from(new Set([...prev, name])));
      onChange(name);
      setNewVal("");
      setAddOpen(false);
      toast.success("Health status added");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="flex gap-2" data-testid={testId}>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none"
        data-testid={`${testId}-input`}
      >
        {allowEmpty && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {canAdd && (
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="h-11 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium inline-flex items-center gap-1"
          data-testid={`${testId}-add-btn`}
          title="Add custom health status"
        >
          <Plus className="h-4 w-4" />
        </button>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setAddOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold">Add Health Status</h2>
              <button type="button" onClick={() => setAddOpen(false)} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-brand-muted mt-2">Add a new clinical condition to the dropdown.</p>
            <div className="mt-3 flex gap-2">
              <input
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                placeholder="e.g. Anemia"
                className="flex-1 h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none"
                data-testid={`${testId}-add-input`}
                autoFocus
              />
              <button type="button" onClick={handleAdd} className="h-11 px-4 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid={`${testId}-add-save`}>
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
